import { formatStoredPath, normalizeRelativePath } from "../domain/paths";

export interface WorkspaceMoveItem {
  readonly kind: "board" | "folder";
  readonly name: string;
  readonly path: readonly string[];
}

export interface WorkspaceMovePlan {
  readonly sourcePath: readonly string[];
  readonly destinationPath: readonly string[];
  readonly destinationFolderPath: readonly string[];
}

/** Builds one safe navigator move while rejecting no-ops and folder ancestry cycles. */
export function planWorkspaceMove(
  item: WorkspaceMoveItem,
  destinationFolderPath: readonly string[],
): WorkspaceMovePlan {
  const sourcePath = normalizeRelativePath(item.path);
  const destinationFolder = normalizeRelativePath(destinationFolderPath);
  const sourceName = sourcePath.at(-1);
  if (sourceName === undefined || sourceName !== item.name) {
    throw new Error("The dragged workspace entry no longer matches its filesystem path.");
  }
  const destinationPath = [...destinationFolder, sourceName];
  if (pathsEqual(sourcePath, destinationPath)) {
    throw new Error(`${item.name} is already in that folder.`);
  }
  if (item.kind === "folder" && isPathAtOrBelow(sourcePath, destinationFolder)) {
    throw new Error("A folder cannot be moved into itself or one of its subfolders.");
  }
  return { sourcePath, destinationPath, destinationFolderPath: destinationFolder };
}

/** Rewrites a candidate at or below one moved source while leaving unrelated paths untouched. */
export function remapWorkspacePath(
  candidatePath: readonly string[],
  sourcePath: readonly string[],
  destinationPath: readonly string[],
): readonly string[] | null {
  const candidate = normalizeRelativePath(candidatePath);
  const source = normalizeRelativePath(sourcePath);
  const destination = normalizeRelativePath(destinationPath);
  if (!isPathAtOrBelow(source, candidate)) {
    return null;
  }
  return [...destination, ...candidate.slice(source.length)];
}

/** Formats the root and nested folder destinations used in feedback copy. */
export function describeWorkspaceFolder(path: readonly string[]): string {
  return path.length === 0 ? "Workspace" : formatStoredPath(path);
}

/** Tests inclusive path ancestry using normalized segment identity. */
function isPathAtOrBelow(ancestor: readonly string[], candidate: readonly string[]): boolean {
  return (
    ancestor.length <= candidate.length &&
    ancestor.every((segment, index) => candidate[index] === segment)
  );
}

/** Tests exact normalized path identity. */
function pathsEqual(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((segment, index) => right[index] === segment);
}
